/**
 * DPL DEW Admin Authentication Library
 * 
 * Handles admin-specific authentication separate from regular user auth
 * This provides the admin authentication layer for platform management
 */

import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/utils/logger';

export interface AdminUser {
  id: string;
  email: string;
  full_name: string;
  role: 'master_admin' | 'operator' | 'support' | 'viewer';
  permissions: string[];
  is_active: boolean;
  last_login_at?: string;
}

export interface AdminSession {
  user: AdminUser;
  token: string;
  expires_at: string;
}

class AdminAuth {
  private static instance: AdminAuth;
  private currentUser: AdminUser | null = null;
  private session: AdminSession | null = null;

  private constructor() {}

  static getInstance(): AdminAuth {
    if (!AdminAuth.instance) {
      AdminAuth.instance = new AdminAuth();
    }
    return AdminAuth.instance;
  }

  /**
   * Sign in admin user with email and password
   */
  async signIn(email: string, password: string, mfaCode?: string): Promise<AdminUser> {
    try {
      // First authenticate with Supabase
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (authError) {
        throw new Error(authError.message);
      }

      if (!authData.user) {
        throw new Error('Authentication failed');
      }

      // Verify this user is an admin in admin_users table
      const { data: adminData, error: adminError } = await supabase
        .from('admin_users')
        .select('*')
        .eq('email', email)
        .eq('is_active', true)
        .single();

      if (adminError || !adminData) {
        // Sign out from Supabase since they're not an admin
        await supabase.auth.signOut();
        throw new Error('Access denied: Not an admin user');
      }

      // Store admin session
      const adminSession: AdminSession = {
        user: {
          id: adminData.id,
          email: adminData.email,
          full_name: adminData.full_name,
          role: adminData.role,
          permissions: adminData.permissions || [],
          is_active: adminData.is_active,
          last_login_at: new Date().toISOString(),
        },
        token: authData.session.access_token,
        expires_at: authData.session.expires_at || '',
      };

      this.session = adminSession;
      this.currentUser = adminSession.user;

      // Store in localStorage for persistence
      localStorage.setItem('admin_session', JSON.stringify(adminSession));
      localStorage.setItem('admin_remember_email', email);

      logger.log('[AdminAuth] Admin signed in successfully:', email);
      return this.currentUser;

    } catch (error) {
      logger.error('[AdminAuth] Sign in failed:', error);
      throw error;
    }
  }

  /**
   * Sign out admin user
   */
  async signOut(): Promise<void> {
    try {
      // Clear Supabase session
      await supabase.auth.signOut();
      
      // Clear admin session
      this.currentUser = null;
      this.session = null;
      
      // Clear localStorage
      localStorage.removeItem('admin_session');
      
      logger.log('[AdminAuth] Admin signed out');
    } catch (error) {
      logger.error('[AdminAuth] Sign out error:', error);
      throw error;
    }
  }

  /**
   * Get current admin user
   */
  getCurrentUser(): AdminUser | null {
    if (this.currentUser) {
      return this.currentUser;
    }

    // Try to restore from localStorage
    const storedSession = localStorage.getItem('admin_session');
    if (storedSession) {
      try {
        const session: AdminSession = JSON.parse(storedSession);
        
        // Check if session is still valid
        if (new Date(session.expires_at) > new Date()) {
          this.session = session;
          this.currentUser = session.user;
          return this.currentUser;
        } else {
          // Session expired, clean up
          localStorage.removeItem('admin_session');
        }
      } catch (error) {
        logger.error('[AdminAuth] Failed to parse stored session:', error);
        localStorage.removeItem('admin_session');
      }
    }

    return null;
  }

  /**
   * Check if user is authenticated as admin
   */
  isAuthenticated(): boolean {
    return this.getCurrentUser() !== null;
  }

  /**
   * Check if current admin has specific role
   */
  hasRole(role: string): boolean {
    const user = this.getCurrentUser();
    return user?.role === role;
  }

  /**
   * Check if current admin has specific permission
   */
  hasPermission(permission: string): boolean {
    const user = this.getCurrentUser();
    if (!user) return false;
    
    // Master admin has all permissions
    if (user.role === 'master_admin') {
      return true;
    }
    
    return user.permissions.includes(permission);
  }

  /**
   * Get current admin session
   */
  getSession(): AdminSession | null {
    return this.session;
  }

  /**
   * Refresh admin session
   */
  async refreshSession(): Promise<void> {
    try {
      const { data: { session }, error } = await supabase.auth.refreshSession();
      
      if (error) {
        throw new Error(error.message);
      }

      if (session && this.currentUser) {
        // Update session token
        if (this.session) {
          this.session.token = session.access_token;
          this.session.expires_at = session.expires_at || '';
          localStorage.setItem('admin_session', JSON.stringify(this.session));
        }
      }
    } catch (error) {
      logger.error('[AdminAuth] Session refresh failed:', error);
      throw error;
    }
  }

  /**
   * Clear all admin data (for testing/debugging)
   */
  clearData(): void {
    this.currentUser = null;
    this.session = null;
    localStorage.removeItem('admin_session');
    localStorage.removeItem('admin_remember_email');
  }
}

// Export singleton instance
export const adminAuth = AdminAuth.getInstance();

// Export types
export type { AdminSession };
